import { useEffect, useState } from "react";
import { Megaphone, Loader2, Users, Send } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { FullSpinner } from "@/components/States";
import { toast } from "sonner";

export default function Broadcast() {
  const [data, setData] = useState(null);
  const [criteria, setCriteria] = useState({ age_min: "", age_max: "", city: "", religion: "", profession: "" });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/broadcast").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);
  if (!data) return <FullSpinner />;
  const set = (k, v) => setCriteria((c) => ({ ...c, [k]: v }));
  const clean = () => Object.fromEntries(Object.entries(criteria).filter(([, v]) => v !== ""));

  const doPreview = async () => {
    try { const { data } = await api.post("/broadcast/preview", { criteria: clean() }); setPreview(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  const send = async () => {
    setBusy(true);
    try { const { data } = await api.post("/broadcast", { criteria: clean() }); toast.success(`Broadcast sent to ${data.sent} members`); setPreview(null); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-5" data-testid="broadcast-page">
      <div>
        <h1 className="font-serif text-3xl font-bold text-[#58111A] flex items-center gap-2"><Megaphone className="w-6 h-6 text-[#C59B27]" /> Profile Broadcast</h1>
        <p className="text-sm text-[#605255]">Share your profile with members who genuinely match your criteria. Strict daily and recipient limits keep it respectful — no mass messaging.</p>
      </div>
      <div className="mj-card p-3 flex items-center justify-between text-sm">
        <span className="text-[#605255]">Remaining today</span>
        <span className="font-semibold text-[#781826]">{data.remaining_today} broadcast(s) · up to {data.cap} members each</span>
      </div>
      <div className="mj-card p-5 grid grid-cols-2 gap-3">
        <div><label className="mj-label">Age Min</label><input className="mj-input" type="number" value={criteria.age_min} onChange={(e) => set("age_min", e.target.value)} /></div>
        <div><label className="mj-label">Age Max</label><input className="mj-input" type="number" value={criteria.age_max} onChange={(e) => set("age_max", e.target.value)} /></div>
        <div><label className="mj-label">City</label><input className="mj-input" value={criteria.city} onChange={(e) => set("city", e.target.value)} /></div>
        <div><label className="mj-label">Religion</label><input className="mj-input" value={criteria.religion} onChange={(e) => set("religion", e.target.value)} /></div>
        <div className="col-span-2"><label className="mj-label">Profession</label><input className="mj-input" value={criteria.profession} onChange={(e) => set("profession", e.target.value)} /></div>
        <button onClick={doPreview} className="btn-outline col-span-2" data-testid="broadcast-preview"><Users className="w-4 h-4" /> Preview Eligible Members</button>
      </div>
      {preview && (
        <div className="mj-card p-5 text-center" data-testid="broadcast-preview-result">
          <div className="font-serif text-3xl font-bold text-[#781826]">{preview.eligible}</div>
          <p className="text-sm text-[#605255] mb-4">eligible members · will notify up to {preview.will_send}</p>
          <button onClick={send} disabled={busy || data.remaining_today <= 0} className="btn-primary w-full" data-testid="broadcast-send">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Confirm & Broadcast</>}</button>
          {data.remaining_today <= 0 && <p className="text-xs text-[#B45309] mt-2">Daily limit reached — try again tomorrow.</p>}
        </div>
      )}
      {data.broadcasts.length > 0 && (
        <div className="mj-card p-5">
          <h2 className="font-serif text-lg font-semibold text-[#261A1C] mb-2">Broadcast History</h2>
          {data.broadcasts.map((b) => (
            <div key={b.id} className="flex justify-between py-2 border-b border-[#F1E9DA] last:border-0 text-sm">
              <span className="text-[#605255]">{new Date(b.created_at).toLocaleString()}</span>
              <span className="text-[#261A1C]">Sent to {b.sent} of {b.eligible} eligible</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
